import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FieldArrayType } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject, map, takeUntil, takeWhile } from 'rxjs';
import { LgvFormUtilityService } from '../../../core/services/lgv-form-utility.service';
import { LgvFormlyModel } from '../../../data-models/formly.model';
import { LgvUserService } from '../../../core/core.module';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { debug } from 'console';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
    selector: 'formly-repeating-section-summary',
    templateUrl: './formly-repeating-section-summary.component.html',
    styleUrls: ['./formly-repeating-section-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
  })

  export class FormlyRepeatingSectionSummaryComponent extends FieldArrayType implements OnInit, AfterViewInit, OnDestroy {

    
    _selectedIndex: number = -1;
    selectedIndex$ = new BehaviorSubject(this._selectedIndex);

    get isSummaryMode$(): Observable<boolean> { return this.selectedIndex$.pipe(map(x => x === -1)) }

    onDestroy$ = new Subject();

    get changeSummaryKey():string {  return this.lgvFormSvc.buildChangeMapKey(this.lgvFormSvc.curLevel1$.value, this.lgvFormSvc.curLevel2$.value); }
    schemaHasConfirmIfNoItems$ = new BehaviorSubject(false);
    schemaConfirmIfNoItemsMessage$ = new BehaviorSubject('');
    numItems$ = new BehaviorSubject(0);
    confirmedNoItemsForForm = false;

    constructor(public lgvFormSvc: LgvFormUtilityService, 
        public matDialog: MatDialog,
        public lgvUserSvc: LgvUserService) {
        super();
    }

    ngOnInit(): void {
        this.confirmedNoItemsForForm = this.lgvFormSvc.haveUserConfirmedNoItems(this.buildUserConfirmNoItemObj());
        this.updateConfirmNoItemsStatus();

        this.field.formControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(x=>{
            this.updateConfirmNoItemsStatus();
        });

    }

    ngAfterViewInit(): void {
        let index = 0;
        for(let arrayItem of this.field.fieldGroup){
            let key = this.lgvFormSvc.buildChangeMapKey(this.lgvFormSvc.curLevel1$.value, -1, index);
            if(!this.lgvFormSvc.hasChangeSummaryForKey(key)){
                this.lgvFormSvc.setChangeSummaryByKey(key, this.lgvFormSvc.buildEmptySummaryObject());
            }
            index++;
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    onEditSection(index: number) {
        // console.log('onEditSection()');
        // console.log(index);
        this.selectedIndex$.next(index);
        if(this.lgvFormSvc.saveOnEachNavigation){
            this.lgvFormSvc.saveForm();
        }

        this.triggerRefresh();
    }

    add() {
        super.add();
        
        let index = this.model.length - 1;
        let key = this.lgvFormSvc.buildChangeMapKey(this.lgvFormSvc.curLevel1$.value, -1, index);
        this.lgvFormSvc.setChangeSummaryByKey(key, this.lgvFormSvc.buildEmptySummaryObject());

        this.onEditSection(index);
    }

    getValue(objArrayIndex: number, fieldKey: string): any {
        /* 
            itemKey is the array index of the repeating item
            fieldKey is the formly "key" (path in the model)
        */

        // test for nested structures like address.city.  Not attempting to go more than a single level deep for now.
        if(fieldKey.includes('.')){
            let parts = fieldKey.split('.');
            if(parts.length === 2){
                if (this.model[objArrayIndex] && this.model[objArrayIndex][parts[0]] 
                    && this.model[objArrayIndex][parts[0]][parts[1]] !== null  
                    && this.model[objArrayIndex][parts[0]][parts[1]] !== '' ) {
                    return this.model[objArrayIndex][parts[0]][parts[1]];
                }
            }
        }

        if (this.model[objArrayIndex] && this.model[objArrayIndex][fieldKey] !== null && this.model[objArrayIndex][fieldKey] !== '') {
            return this.model[objArrayIndex][fieldKey];
        }
        
        return null;
    }


    
    onConfirmRemoveArrayObject(index:number, obj: LgvFormlyModel.Object) {

        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: { Confirmation: `Are you sure you want to remove this ${obj?.props?.label} item?` }
        });

        dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(x => {
            if (x.action === true) {
                let key = this.lgvFormSvc.buildChangeMapKey(this.lgvFormSvc.curLevel1$.value, -1, index);
                this.lgvFormSvc.removeSummaryForKey(key);
                this.remove(index);
            }
        });

    }

    onFormChange(e){
        let fieldObject = this.field.fieldGroup[this.selectedIndex$.value];

        if(!fieldObject || !fieldObject.fieldGroup || !fieldObject.fieldGroup.length ){
            return;
        }

        this.lgvFormSvc.updateChangeMap(fieldObject.fieldGroup, this.selectedIndex$.value);
    }    


    //#region Confirm No Itmes

    updateConfirmNoItemsStatus() {

        if (this.field?.props['confirmIfNoItems']) {
            // console.log('CONFIRM IF NO ITEMS');
            this.schemaHasConfirmIfNoItems$.next(true);
        }

        if (this.field?.props['confirmIfNoItemsMessage']) {
            let msg = this.field?.props['confirmIfNoItemsMessage'];
            this.schemaConfirmIfNoItemsMessage$.next(msg);
        }

        if (this.field?.fieldGroup?.length) {
            this.numItems$.next(this.field?.fieldGroup?.length);
        }
        else {
            this.numItems$.next(0);
        }
        this.lgvFormSvc.updateChangeMap(this.field.parent.fieldGroup);
    }

    buildUserConfirmNoItemObj(): LgvFormlyModel.UserConfirmedNoItem{
        return { changeSummaryKey: this.changeSummaryKey, modelKey: this.field.key.toString() }
    }

    onConfirmChanged(event: MatCheckboxChange) {
        // if schema has confirmIfNoItems, then it's a validation error if the array has no items.  The list of user confirmations is save in lgvFormSvc.confirmedNoItemsList$
        let summary: LgvFormlyModel.GroupSummary;

        if (event.checked) {
            this.lgvFormSvc.addConfirmedNoItems(this.buildUserConfirmNoItemObj());

        } else {
            this.lgvFormSvc.removeConfirmedNoItems(this.buildUserConfirmNoItemObj());
        }

        this.lgvFormSvc.updateChangeMap(this.field.parent.fieldGroup);
    }
 
    private triggerRefresh() {
        setTimeout(()=>{
            this.lgvFormSvc.triggerDataModelRefresh();
        },250);
     }

    //#endregion Confirm No Items
}